import _ from "lodash";
import { PlatformTypes, Post } from "../features/post";
import { PostFormState } from "../shared/types/post";

export interface PostFormRootState {
  forms: {
    postForms: {
      byId: Record<string, PostFormState>;
      allIds: string[];
    };
  };
}

export const getFirstForm = (
  state: PostFormRootState,
  formId: string | null = null
) => {
  const id = formId ? formId : state.forms.postForms.allIds[0];
  return state.forms.postForms.byId[id];
};

export const getFormById = (state: PostFormRootState, formId: string) => {
  return state.forms.postForms.byId[formId];
};

export const getOriginalPost = (
  state: PostFormRootState,
  formId: string | null = null
) => {
  const form = formId ? getFormById(state, formId) : getFirstForm(state);
  return form ? form.originalPost : undefined;
};

export const getOriginalPostState = (
  state: PostFormRootState,
  formId: string | null = null
) => {
  const form = formId ? getFormById(state, formId) : getFirstForm(state);
  return form ? form.originalPostState : undefined;
};

export const getFormPost = (
  state: PostFormRootState,
  formId: string | null = null
) => {
  const form = formId ? getFormById(state, formId) : getFirstForm(state);
  return form ? form.post : undefined;
};

export const getDescriptions = (
  state: PostFormRootState,
  formId: string | null = null
) => {
  const form = formId ? getFormById(state, formId) : getFirstForm(state);
  if (form && form.post.mediaFiles) {
    const descriptions = form.post.mediaFiles.map((mediaFile) => {
      return mediaFile.description
        ? {
            description: mediaFile.description,
            thumbnailUrl: mediaFile.thumbnailUrl,
          }
        : null;
    });

    return _.compact(descriptions);
  } else {
    return [];
  }
};

export const getFormAccounts = (
  state: PostFormRootState,
  formId: string | null = null
) => {
  const form = formId ? getFormById(state, formId) : getFirstForm(state);
  return form ? form.accounts : undefined;
};

export const getPostState = (
  state: PostFormRootState,
  formId: string | null = null
) => {
  const form = formId ? getFormById(state, formId) : getFirstForm(state);
  return form ? form.postState : undefined;
};

export const getSelectedPlatform = (
  state: PostFormRootState,
  formId: string | null = null
) => {
  const form = formId ? getFormById(state, formId) : getFirstForm(state);
  return form ? form.view.selectedPlatform : undefined;
};

export const getVisibleFields = (
  state: PostFormRootState,
  formId: string | null = null
) => {
  const form = formId ? getFormById(state, formId) : getFirstForm(state);
  return form ? form.view.visibleFields : undefined;
};

export const getFormErrors = (
  state: PostFormRootState,
  formId: string | null = null
) => {
  const form = formId ? getFormById(state, formId) : getFirstForm(state);
  return form ? form.validation.errors : undefined;
};

export const getFormWarnings = (
  state: PostFormRootState,
  formId: string | null = null
) => {
  const form = formId ? getFormById(state, formId) : getFirstForm(state);
  return form ? form.validation.warnings : undefined;
};

export const getPlatforms = (
  state: PostFormRootState,
  formId: string | null = null
) => {
  const form = formId ? getFormById(state, formId) : getFirstForm(state);
  return form ? form.platforms : [];
};

export const getPlatformFields = (
  state: PostFormRootState,
  formId: string | null = null
) => {
  const form = formId ? getFormById(state, formId) : getFirstForm(state);
  return form ? form.platformFields : undefined;
};

export const getPlatformPost = (
  state: PostFormRootState,
  formId: string | null = null
) => {
  const form = formId ? getFormById(state, formId) : getFirstForm(state);
  if (!form) {
    return;
  }
  let post = { ...form.post, _id: formId } as Post;
  const selectedPlatform = form.view.selectedPlatform;

  const platformTypes = _.map(form.platforms, "TYPE");
  if (selectedPlatform === "LINKED") {
    post["platformType"] = (
      platformTypes.includes("IG") ? "IG" : post.platformType
    ) as PlatformTypes;
  } else {
    post = {
      ...post,
      ...form.platformFields[selectedPlatform],
      platformType: selectedPlatform,
    } as Post;
  }

  return post;
};

export const getFormResults = (
  state: PostFormRootState,
  formId: string | null = null
) => {
  const form = formId ? getFormById(state, formId) : getFirstForm(state);
  return form?.results ?? null;
};
